import React, { useState, useEffect } from "react";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Copy, Check, RotateCcw } from "lucide-react";

// Robust Lightweight TOML Parser and Stringifier to avoid React 19 version mismatches
function parseToml(toml: string): any {
  const result: any = {};
  let currentContext = result;
  let currentContextPath: string[] = [];

  const lines = toml.split("\n");

  const parseValue = (valStr: string): any => {
    valStr = valStr.trim();
    if (valStr === "true") return true;
    if (valStr === "false") return false;
    if (valStr.startsWith('"') && valStr.endsWith('"')) {
      return valStr.slice(1, -1);
    }
    if (valStr.startsWith("'") && valStr.endsWith("'")) {
      return valStr.slice(1, -1);
    }
    if (valStr.startsWith("[") && valStr.endsWith("]")) {
      // Inline Array
      const elements = valStr.slice(1, -1).split(",");
      return elements.map(el => parseValue(el.trim())).filter(el => el !== "");
    }
    const num = Number(valStr);
    if (!isNaN(num)) return num;
    return valStr; // Fallback to raw string
  };

  const setNestedKey = (obj: any, path: string[], value: any) => {
    let current = obj;
    for (let i = 0; i < path.length - 1; i++) {
      const part = path[i];
      if (!current[part] || typeof current[part] !== "object") {
        current[part] = {};
      }
      current = current[part];
    }
    const lastPart = path[path.length - 1];
    current[lastPart] = value;
  };

  const getNestedOrCreate = (obj: any, path: string[], isArrayOfTables = false) => {
    let current = obj;
    for (let i = 0; i < path.length; i++) {
      const part = path[i];
      const isLast = i === path.length - 1;

      if (isLast && isArrayOfTables) {
        if (!current[part]) {
          current[part] = [];
        }
        if (!Array.isArray(current[part])) {
          current[part] = [current[part]];
        }
        const newTable = {};
        current[part].push(newTable);
        return newTable;
      }

      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }
    return current;
  };

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    // Skip empty and comments
    if (!line || line.startsWith("#")) continue;

    // Handle array of tables: [[section]]
    if (line.startsWith("[[") && line.endsWith("]]")) {
      const sectionName = line.slice(2, -2).trim();
      const path = sectionName.split(".").map(s => s.trim());
      currentContextPath = path;
      currentContext = getNestedOrCreate(result, path, true);
      continue;
    }

    // Handle table sections: [section]
    if (line.startsWith("[") && line.endsWith("]")) {
      const sectionName = line.slice(1, -1).trim();
      const path = sectionName.split(".").map(s => s.trim());
      currentContextPath = path;
      currentContext = getNestedOrCreate(result, path, false);
      continue;
    }

    // Handle key = value
    const eqIdx = line.indexOf("=");
    if (eqIdx !== -1) {
      const key = line.slice(0, eqIdx).trim();
      let valStr = line.slice(eqIdx + 1).trim();
      
      // Basic inline comments removal
      const hashIdx = valStr.indexOf("#");
      if (hashIdx !== -1) {
        // check if inside quotes
        const doubleQuoteCount = (valStr.slice(0, hashIdx).match(/"/g) || []).length;
        if (doubleQuoteCount % 2 === 0) {
          valStr = valStr.slice(0, hashIdx).trim();
        }
      }

      const parsedVal = parseValue(valStr);
      currentContext[key] = parsedVal;
    }
  }

  return result;
}

function stringifyToToml(obj: any, prefix = ""): string {
  let toml = "";
  const primitives: [string, any][] = [];
  const objects: [string, any][] = [];
  const arraysOfObjects: [string, any[]][] = [];

  if (!obj || typeof obj !== "object") return "";

  Object.entries(obj).forEach(([key, val]) => {
    if (val === null || val === undefined) return;
    if (Array.isArray(val)) {
      if (val.length > 0 && typeof val[0] === "object") {
        arraysOfObjects.push([key, val]);
      } else {
        primitives.push([key, val]);
      }
    } else if (typeof val === "object") {
      objects.push([key, val]);
    } else {
      primitives.push([key, val]);
    }
  });

  // Print primitive variables first
  primitives.forEach(([key, val]) => {
    if (typeof val === "string") {
      toml += `${key} = "${val.replace(/"/g, '\\"')}"\n`;
    } else if (Array.isArray(val)) {
      const elements = val.map(el => {
        if (typeof el === "string") return `"${el.replace(/"/g, '\\"')}"`;
        return String(el);
      }).join(", ");
      toml += `${key} = [${elements}]\n`;
    } else {
      toml += `${key} = ${val}\n`;
    }
  });

  if (primitives.length > 0) toml += "\n";

  // Print tables next
  objects.forEach(([key, val]) => {
    const fullHeader = prefix ? `${prefix}.${key}` : key;
    toml += `[${fullHeader}]\n`;
    toml += stringifyToToml(val, fullHeader);
  });

  // Print arrays of tables
  arraysOfObjects.forEach(([key, arr]) => {
    const fullHeader = prefix ? `${prefix}.${key}` : key;
    arr.forEach(item => {
      toml += `[[${fullHeader}]]\n`;
      toml += stringifyToToml(item, fullHeader);
    });
  });

  return toml.trim() + "\n";
}

export function TomlJsonConverter() {
  const [mode, setMode] = useState<"toml2json" | "json2toml">("toml2json");
  const [tomlInput, setTomlInput] = useState(
    `# Cargo.toml configuration template\n[package]\nname = "cool-rust-project"\nversion = "1.2.0"\nedition = "2021"\nauthors = ["Developer X", "Team Y"]\n\n[dependencies]\nserde = { version = "1.0", features = ["derive"] }\ntokio = "1.28"\n\n[[bin]]\nname = "app-cli"\npath = "src/main.rs"`
  );
  const [jsonInput, setJsonInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mode === "toml2json") {
      try {
        setError(null);
        if (!tomlInput.trim()) {
          setJsonInput("");
          return;
        }
        const parsed = parseToml(tomlInput);
        setJsonInput(JSON.stringify(parsed, null, 2));
      } catch (err: any) {
        setError("TOML Parser Warning: Please double check TOML format brackets and quotes.");
      }
    }
  }, [tomlInput, mode]);

  useEffect(() => {
    if (mode === "json2toml") {
      try {
        setError(null);
        if (!jsonInput.trim()) {
          setTomlInput("");
          return;
        }
        const parsed = JSON.parse(jsonInput);
        const generated = stringifyToToml(parsed);
        setTomlInput(generated);
      } catch (err: any) {
        setError(`JSON Parser Error: ${err.message}`);
      }
    }
  }, [jsonInput, mode]);

  const handleCopy = () => {
    const textToCopy = mode === "toml2json" ? jsonInput : tomlInput;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setTomlInput("");
    setJsonInput("");
    setError(null);
  };

  const handlePreset = (type: string) => {
    if (type === "cargo") {
      setMode("toml2json");
      setTomlInput(
        `[package]\nname = "my-library"\nversion = "0.4.1"\n\n[dependencies]\nrand = "0.8.5"\nreqwest = ["json", "blocking"]`
      );
    } else if (type === "pyproject") {
      setMode("toml2json");
      setTomlInput(
        `[tool.poetry]\nname = "web-scraper"\nversion = "1.0.0"\ndescription = "A fast Python web scraper"\n\n[tool.poetry.dependencies]\npython = "^3.9"\nbeautifulsoup4 = "^4.11"`
      );
    } else if (type === "simple_json") {
      setMode("json2toml");
      setJsonInput(
        `{\n  "database": {\n    "host": "127.0.0.1",\n    "port": 5432,\n    "enabled": true\n  },\n  "ports": [80, 443]\n}`
      );
    }
  };

  return (
    <ToolPageLayout
      title="Free Online TOML to JSON & JSON to TOML Converter"
      description="Convert config settings between TOML markup format and JSON objects inside your browser. Enjoy dynamic structural styling, instant outputs, and absolute privacy."
      h1="TOML ↔ JSON Converter"
      category="JSON / Data"
      categoryPath="/json-tools"
      howToUse={[
        "Choose your conversion direction: TOML to JSON or JSON to TOML using the options bar.",
        "Paste or type your data configurations into the active input panel.",
        "Any syntax errors are parsed instantly with detailed error warning alerts.",
        "Copy or download the output clean structured configuration directly."
      ]}
      faq={[
        { question: "What is TOML used for?", answer: "TOML (Tom's Obvious Minimal Language) is a minimal configuration file format designed to be easy to read and write due to its clear key-value structure." },
        { question: "How to convert TOML config to JSON online?", answer: "Select 'TOML to JSON' mode, paste your TOML string (like Cargo.toml), and get immediate, beautifully indented JSON outputs." },
        { question: "What is the key difference between TOML and JSON?", answer: "TOML emphasizes human-readability with headers and inline arrays, while JSON is an object-oriented format optimized for computer parsing." },
        { question: "Does this TOML converter support nested tables?", answer: "Yes. Tables like [database.settings] are accurately converted into nested JSON objects." },
        { question: "How does TOML represent arrays of tables?", answer: "It uses double brackets, e.g. [[products]], to create a list of objects mapped to the same key name." },
        { question: "Can I convert pyproject.toml and Cargo.toml files?", answer: "Absolutely. Paste any Rust, Python, or Webpack configuration to translate keys instantly." },
        { question: "Is this TOML encoder safe for sensitive secrets?", answer: "Yes. All code runs client-side inside your local browser. No data leaves your machine." },
        { question: "Does TOML support comments?", answer: "Yes, comments begin with the '#' symbol. Our parser skips comments cleanly during JSON creation." },
        { question: "What happens if my JSON syntax is invalid?", answer: "Our validator outputs the precise character location or line number of the missing brace or double quote." },
        { question: "Is there a file size limit for converting TOML?", answer: "Since operations run inside browser RAM, there's virtually no limit, though extremely massive database files could cause slight delay." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-white border border-slate-200 rounded-xl">
          <div className="flex gap-2">
            <button
              onClick={() => setMode("toml2json")}
              className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                mode === "toml2json"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-slate-100 hover:bg-slate-200 text-slate-700"
              }`}
            >
              TOML ➔ JSON
            </button>
            <button
              onClick={() => setMode("json2toml")}
              className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                mode === "json2toml"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-slate-100 hover:bg-slate-200 text-slate-700"
              }`}
            >
              JSON ➔ TOML
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handlePreset("cargo")}
              className="text-xs bg-slate-50 hover:bg-slate-100 text-slate-600 px-2.5 py-1.5 rounded border transition-colors"
            >
              Preset: Cargo.toml
            </button>
            <button
              onClick={() => handlePreset("pyproject")}
              className="text-xs bg-slate-50 hover:bg-slate-100 text-slate-600 px-2.5 py-1.5 rounded border transition-colors"
            >
              Preset: pyproject.toml
            </button>
            <button
              onClick={() => handlePreset("simple_json")}
              className="text-xs bg-slate-50 hover:bg-slate-100 text-slate-600 px-2.5 py-1.5 rounded border transition-colors"
            >
              Preset: JSON Config
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-rose-50 border border-rose-100 text-rose-800 rounded-xl text-xs font-semibold">
            ⚠️ {error}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="font-bold text-slate-800">
                {mode === "toml2json" ? "TOML Source Markup" : "JSON Input Data"}
              </Label>
              <button
                onClick={handleClear}
                className="text-xs text-rose-500 hover:text-rose-600 flex items-center gap-1 font-bold"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Clear
              </button>
            </div>
            {mode === "toml2json" ? (
              <Textarea
                value={tomlInput}
                onChange={(e) => setTomlInput(e.target.value)}
                rows={12}
                className="font-mono text-xs bg-slate-50 border-slate-200"
                placeholder="# Paste your TOML configurations here..."
              />
            ) : (
              <Textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                rows={12}
                className="font-mono text-xs bg-slate-50 border-slate-200"
                placeholder='{\n  "name": "project"\n}'
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="font-bold text-slate-800">
                {mode === "toml2json" ? "JSON Format Output" : "TOML Markup Output"}
              </Label>
              <button
                onClick={handleCopy}
                disabled={!!error || (mode === "toml2json" ? !jsonInput : !tomlInput)}
                className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy Code
                  </>
                )}
              </button>
            </div>
            {mode === "toml2json" ? (
              <pre className="p-4 rounded-xl border border-slate-200 font-mono text-xs overflow-x-auto bg-slate-900 text-slate-100 min-h-[220px]">
                <code>{jsonInput || "// Your JSON will appear here..."}</code>
              </pre>
            ) : (
              <pre className="p-4 rounded-xl border border-slate-200 font-mono text-xs overflow-x-auto bg-slate-900 text-slate-100 min-h-[220px]">
                <code>{tomlInput || "# Your TOML will appear here..."}</code>
              </pre>
            )}
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
